import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatR, pnlClass } from '../trackRecord/recordUI'
import FeeInput from './FeeInput'
import { FEE_REFETCH_DEBOUNCE_MS, formatFeePct } from '../../config/fees'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import useStartingFee from '../../hooks/useStartingFee'
import { FEE_CALCULATOR_PATH } from '../../routes'
import { publicCreatorsAPI } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { FeeCalculatorRecord } from '../../types'
import { formatPnlPct } from '../../utils/pnl'

const pct = (value: number | null | undefined) => (value == null ? '—' : `${value.toFixed(1)}%`)
const r = (value: number | null | undefined) => (value == null ? '—' : formatR(value))
const sum = (value: number | null | undefined) => (value == null ? '—' : formatPnlPct(value))
const tone = (value: number | null | undefined) => (value == null ? 'text-gray-500' : pnlClass(value))

/**
 * A creator's closed calls netted at the reader's fee, beside the gross
 * figures. The Fee Calculator's control (FeeInput), its starting-fee rule
 * (useStartingFee) and its backend calculation (net_record, through
 * GET /api/public/creators/by-id/{id}/fee-calculator): nothing here computes
 * a net figure. A what-if only; the fee is saved on the Fee Calculator.
 */
const CreatorFeePanel = ({ creatorId }: { creatorId: number }) => {
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const { fee, setFee } = useStartingFee(signedIn)
  const settled = useDebouncedValue(fee, FEE_REFETCH_DEBOUNCE_MS)
  const [record, setRecord] = useState<FeeCalculatorRecord | null>(null)
  const [failed, setFailed] = useState(false)
  const latest = useRef(0)

  useEffect(() => {
    if (settled == null) return
    const id = ++latest.current
    publicCreatorsAPI
      .feeCalculator(creatorId, settled)
      .then((data) => {
        if (id !== latest.current) return
        setRecord(data)
        setFailed(false)
      })
      .catch((error) => {
        console.error('Creator fee figures failed to load:', error)
        if (id === latest.current) setFailed(true)
      })
  }, [creatorId, settled])

  const pending = record == null || fee == null || record.fee_pct_per_side !== fee
  const rows = record
    ? [
        {
          label: 'Win rate',
          testId: 'fee-win-rate',
          gross: pct(record.win_rate),
          net: pct(record.win_rate_after_fees),
          grossTone: 'text-gray-100',
          netTone: 'text-gray-100',
        },
        {
          label: 'Avg R',
          testId: 'fee-avg-r',
          gross: r(record.avg_gross_r),
          net: r(record.avg_net_r),
          grossTone: tone(record.avg_gross_r),
          netTone: tone(record.avg_net_r),
        },
        {
          label: 'Sum of trade returns',
          testId: 'fee-sum',
          gross: sum(record.sum_gross_pnl_pct),
          net: sum(record.sum_net_pnl_pct),
          grossTone: tone(record.sum_gross_pnl_pct),
          netTone: tone(record.sum_net_pnl_pct),
        },
      ]
    : []

  return (
    <section className="space-y-3" aria-labelledby="creator-fee-title" data-testid="creator-fee-panel">
      <div>
        <h2 id="creator-fee-title" className="text-sm font-semibold text-gray-100">
          After your fee
        </h2>
        <p className="text-xs text-gray-500">
          Everything above is before fees. Enter yours to see what it does to these calls: the same calculation as
          the{' '}
          <Link to={FEE_CALCULATOR_PATH} className="text-sky-300 underline hover:text-sky-200">
            fee calculator
          </Link>
          , where you can save it.
        </p>
      </div>
      {fee == null ? (
        <p className="text-sm text-gray-500" data-testid="fee-loading">
          Loading your saved fee…
        </p>
      ) : (
        <FeeInput value={fee} onChange={setFee} />
      )}
      {failed && record == null && (
        <p className="text-sm text-red-300" role="alert">
          The figures after fees could not be loaded right now.
        </p>
      )}
      {record && (
        <div className={`transition-opacity ${pending ? 'opacity-60' : ''}`} aria-busy={pending}>
          <table className="w-full text-sm" data-testid="creator-fee-table">
            <thead>
              <tr className="border-b border-terminal-border text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="py-2 pr-3 font-medium" />
                <th className="py-2 pr-3 text-right font-medium">Before fees</th>
                <th className="py-2 text-right font-medium">After {formatFeePct(record.fee_pct_per_side)} per side</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-terminal-border">
              {rows.map((row) => (
                <tr key={row.label} data-testid={row.testId}>
                  <td className="py-2 pr-3 text-gray-400">{row.label}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${row.grossTone}`} data-testid={`${row.testId}-gross`}>
                    {row.gross}
                  </td>
                  <td className={`py-2 text-right font-mono tabular-nums ${row.netTone}`} data-testid={`${row.testId}-net`}>
                    {row.net}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500" data-testid="creator-fee-basis">
            {record.basis} Over {record.closed} closed call{record.closed === 1 ? '' : 's'}; win rate after fees counts a
            call whose return after the fee is above zero.
          </p>
        </div>
      )}
    </section>
  )
}

export default CreatorFeePanel
